import { type ReactNode } from 'react';
import { TrendingUp } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useActivePricing, type Tier } from '@/hooks/use-pricing';
import { TierRowReadonly } from './pricing-tier-row-readonly';
import { HelpTip } from './help-tip';

const PRICING_HELP =
  'Cuantos mas creadores añadas en un dia, mas ganas por cada uno. Por ejemplo, si añades 5 creadores en un dia ganas $0,15 por cada uno ($0,75 ese dia), pero si añades 25 ya estas en el siguiente tramo y ganas $0,20 por cada uno ($5 ese dia). El precio sube automaticamente segun cuantos hagas en un mismo dia.';

function findTier(count: number, tiers: Tier[]): Tier | undefined {
  if (count <= 0) return tiers[0];
  return tiers.find(
    (t) => count >= t.min && (t.max === null || count <= t.max)
  );
}

function Header(): ReactNode {
  return (
    <div className="flex items-center gap-sm">
      <TrendingUp size={14} className="text-text-tertiary/60" />
      <p className="text-[10px] font-medium uppercase tracking-wider text-text-tertiary/60">
        Precios y ganancias por día
        <span className="ml-0.5 inline-block normal-case">
          <HelpTip text={PRICING_HELP} />
        </span>
      </p>
    </div>
  );
}

type Props = { todayCreators?: number | undefined };

function TiersList({
  tiers,
  active,
  todayCreators,
}: {
  tiers: Tier[];
  active: Tier | undefined;
  todayCreators: number | undefined;
}): ReactNode {
  return (
    <div className="mt-sm space-y-px">
      {tiers.map((t) => {
        const isActive = active?.min === t.min;
        return (
          <TierRowReadonly
            key={t.min}
            tier={t}
            active={isActive}
            todayCreators={isActive ? todayCreators : undefined}
          />
        );
      })}
    </div>
  );
}

export function PricingTiersReadonly({ todayCreators }: Props = {}): ReactNode {
  const { data, isLoading } = useActivePricing();
  if (isLoading) return <Skeleton className="h-32 rounded-xl" />;
  if (!data) return null;
  const active =
    todayCreators !== undefined
      ? findTier(todayCreators, data.tiers)
      : undefined;
  return (
    <div className="rounded-xl bg-white/[0.02] p-base">
      <Header />
      <TiersList
        tiers={data.tiers}
        active={active}
        todayCreators={todayCreators}
      />
    </div>
  );
}
